import React, { type ReactElement } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import Loading from 'atom-components/loading';
import FullName from 'feature-components/full-name';
import SystemErrorAlert from 'feature-components/system-error-alert';
import ReallocateResponseForm from 'pages/marking/components/reallocate-response-form';
import { organisationUsersWithMarkerRoleParams } from 'pages/marking/helpers/request-params.helpers';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import { useAuthCustomer } from 'services/providers/auth.hooks';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onReallocate: () => void;
  responseId: number;
  allocatedMarkerId: number;
  allocatedOrganisationUser: BaseCustomerUser | undefined;
  isLoading: boolean;
};

export default function ReallocateResponseModal({
  isOpen,
  onClose,
  onReallocate,
  responseId,
  allocatedMarkerId,
  allocatedOrganisationUser,
  isLoading
}: Props): ReactElement {
  // Fetch any organisation user who has the MARKER role. Further refinements will be done to hide
  // already allocated markers from this list that is kept in sync each time the form is submitted.
  const currentOrganisation = useAuthCustomer();
  const organisationUsersQuery = useCustomerUsers(
    organisationUsersWithMarkerRoleParams(currentOrganisation?.id),
    { enabled: !!currentOrganisation?.id }
  );
  const organisationUsers = organisationUsersQuery.data?.json;

  return (
    <Modal isOpen={isOpen} size="md">
      <ModalHeader toggle={onClose}>Reallocate Response</ModalHeader>
      <ModalBody>
        {isLoading || (organisationUsersQuery.isLoading && <Loading />)}
        {!isLoading && organisationUsersQuery.isSuccess && (
          <>
            <p>
              Please select a new marker to be allocated to <b>response #{responseId}</b> instead of{' '}
              <FullName organisationUser={allocatedOrganisationUser} bringAttentionTo />.
            </p>
            {!allocatedOrganisationUser && (
              <SystemErrorAlert>Organisation user to deallocate not found.</SystemErrorAlert>
            )}
            {allocatedOrganisationUser && (
              <ReallocateResponseForm
                responseId={responseId}
                oldMarkerId={allocatedMarkerId}
                oldOrganisationUser={allocatedOrganisationUser}
                organisationUsers={organisationUsers || []}
                onClose={onClose}
                onReallocate={onReallocate}
              />
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
}
